import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { NoticeBanner, NoticeBannerLabels, NoticeFullBody } from '@shared/designSystem/NoticeBanner'
import { NoticeAnnouncer, NoticeBar, NoticeCountPill, NoticePanel, NoticeSummaryRow } from '@shared/designSystem/NoticeBar'
import { Modal } from '@shared/designSystem/messages/Modal'
import { NormalButton } from '@shared/designSystem/Button'
import { HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers'
import designSystemStyles from '@shared/styles/designSystemStyles'
import {
    PlatformNoticesRealtimeService,
    PlatformNoticesRealtimeServiceInterface,
} from '@services/PlatformNoticesRealtimeService'
import { Organization } from '@models/Organization'
import { PlatformNoticeDisplay, usePlatformNoticesViewModel } from './PlatformNoticesViewModel'

/** Breakpoints on the content pane (not the viewport): the sidebar takes 200–430px. */
const FULL_PANE_MIN_WIDTH = 760
const COMPACT_PANE_MIN_WIDTH = 480

type Density = 'full' | 'compact' | 'summary'

interface PlatformNoticesBarProps {
    organization?: Organization
    organizationCount: number
    uid?: string
    service?: PlatformNoticesRealtimeServiceInterface
}

export const PlatformNoticesBar: React.FC<PlatformNoticesBarProps> = ({ organization, organizationCount, uid, service }) => {
    const location = useLocation()
    const realtimeService = useMemo(() => service ?? new PlatformNoticesRealtimeService(), [service])
    const viewModel = usePlatformNoticesViewModel({
        service: realtimeService,
        uid,
        organizationID: organization?.id,
        organizationName: organization?.name,
        countryCode: organization?.countryCode,
        hasMultipleOrganizations: organizationCount > 1,
        navigationKey: location.pathname,
    })
    const { strings } = viewModel

    const barRef = useRef<HTMLElement>(null)
    const pillRef = useRef<HTMLButtonElement>(null)
    const [paneWidth, setPaneWidth] = useState<number>(FULL_PANE_MIN_WIDTH)

    useEffect(() => {
        const element = barRef.current
        if (!element || typeof ResizeObserver === 'undefined') {
            return
        }
        const observer = new ResizeObserver(([entry]) => setPaneWidth(entry.contentRect.width))
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    const density: Density = paneWidth >= FULL_PANE_MIN_WIDTH
        ? 'full'
        : paneWidth >= COMPACT_PANE_MIN_WIDTH ? 'compact' : 'summary'

    const labelsFor = useCallback((display: PlatformNoticeDisplay): NoticeBannerLabels => ({
        level: strings.levels[display.level],
        more: strings.more,
        less: strings.less,
        dismiss: strings.dismiss(display.title),
    }), [strings])

    const dismiss = useCallback((display: PlatformNoticeDisplay) => {
        viewModel.dismiss(display.notice)
        // Keep keyboard users inside the bar: move to the next row's first control.
        window.requestAnimationFrame(() => {
            const next = barRef.current?.querySelector<HTMLElement>('a, button')
            next?.focus()
        })
    }, [viewModel])

    const pill = viewModel.hiddenCount > 0 ? (
        <NoticeCountPill
            ref={pillRef}
            label={strings.moreCount(viewModel.hiddenCount)}
            expanded={viewModel.panelOpen}
            onClick={viewModel.togglePanel}
            pulseKey={viewModel.pulseKey}
        />
    ) : undefined

    const panel = viewModel.panelOpen ? (
        <NoticePanel
            title={strings.panelTitle(viewModel.allNotices.length)}
            closeLabel={strings.panelClose}
            onClose={viewModel.closePanel}
            sheet={density === 'summary'}
            returnFocusTo={pillRef}
        >
            {viewModel.allNotices.map((display) => (
                <NoticeBanner
                    key={display.key}
                    variant="card"
                    level={display.level}
                    title={display.title}
                    body={display.body}
                    meta={display.meta}
                    cta={display.cta}
                    labels={labelsFor(display)}
                    collapsible={false}
                    onDismiss={display.canDismiss ? () => viewModel.dismiss(display.notice) : undefined}
                    onLinkClick={(href) => viewModel.linkClicked(display.notice, href)}
                />
            ))}
        </NoticePanel>
    ) : undefined

    const ackNotice = viewModel.ackNotice

    return (
        <>
            <NoticeBar ref={barRef} ariaLabel={strings.panelTitle(viewModel.allNotices.length)} hasContent={viewModel.allNotices.length > 0} panel={panel}>
                {density === 'summary' && viewModel.topNotice ? (
                    <NoticeSummaryRow
                        ref={pillRef}
                        level={viewModel.topNotice.level}
                        levelLabel={strings.levels[viewModel.topNotice.level]}
                        title={viewModel.topNotice.title}
                        countLabel={viewModel.allNotices.length > 1 ? `+${viewModel.allNotices.length - 1}` : ''}
                        expanded={viewModel.panelOpen}
                        onClick={viewModel.togglePanel}
                    />
                ) : (
                    viewModel.rows.map((display, index) => (
                        <NoticeBanner
                            key={display.key}
                            variant="bar"
                            density={density === 'compact' ? 'compact' : 'full'}
                            level={display.level}
                            title={display.title}
                            body={display.body}
                            meta={display.meta}
                            cta={display.cta}
                            labels={labelsFor(display)}
                            trailing={index === viewModel.rows.length - 1 ? pill : undefined}
                            onDismiss={display.canDismiss ? () => dismiss(display) : undefined}
                            onLinkClick={(href) => viewModel.linkClicked(display.notice, href)}
                        />
                    ))
                )}
                <NoticeAnnouncer message={viewModel.announcement} />
            </NoticeBar>
            <Modal
                isOpen={ackNotice !== undefined}
                onClose={() => undefined}
                dismissible={false}
                title={ackNotice?.title ?? ''}
                subtitle={viewModel.ackSubtitle}
                width={designSystemStyles.noticeAckModalWidth}
            >
                {ackNotice ? (
                    <VerticalContainer gap={designSystemStyles.gapMd} alignment="stretch" width="100%">
                        <NoticeFullBody
                            body={ackNotice.body}
                            cta={ackNotice.cta}
                            onLinkClick={(href) => viewModel.linkClicked(ackNotice.notice, href)}
                        />
                        <HorizontalContainer justifyContent="flex-end" width="100%">
                            <NormalButton text={strings.acknowledge} onClick={viewModel.acknowledge} />
                        </HorizontalContainer>
                    </VerticalContainer>
                ) : null}
            </Modal>
        </>
    )
}

export default PlatformNoticesBar
